import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import AppIcon from "../../../components/ui/AppIcon";
import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";
import { storeChats, uploadFile } from "../../../services/chatServices";
import { sendChannelMessage } from "../../../services/channelService";
import { deleteDraft as deleteDraftAPI } from "../../../services/draftService";

export default function DraftsModal({ onClose }) {
  const { user } = useAuth();
  const { userDrafts, channels, directMessages, activeChat, selectChat, refreshData, refetchDrafts } = useChat();

  const [activeTab, setActiveTab] = useState("drafts");
  const [sendingDraftId, setSendingDraftId] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const itemRefs = useRef([]);
  const handleLoadDraftRef = useRef(null);

  const unsentDrafts = userDrafts.filter(d => !d.scheduledAt);
  const scheduledDrafts = userDrafts.filter(d => d.scheduledAt);

  const getChatInfo = useCallback((draft) => {
    const { chatId, chatType } = draft;
    if (chatType === 'channel') {
      const channel = channels.find(c => (c._id || c.id).toString() === chatId.toString());
      if (channel) return { name: channel.name, avatar: null, type: 'channel', data: channel };
    } else {
      const dm = directMessages.find(d => (d._id || d.id).toString() === chatId.toString());
      if (dm) return { name: dm.name || dm.displayName, avatar: dm.profilePhoto, type: 'dm', data: dm };
    }
    return { name: 'Unknown Chat', avatar: null, type: null, data: null };
  }, [channels, directMessages]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleLoadDraft = (draft) => {
    const { chatId, chatType } = draft;
    let chatData = null;
    if (chatType === 'channel') {
      chatData = channels.find(c => (c._id || c.id).toString() === chatId.toString());
    } else {
      chatData = directMessages.find(d => (d._id || d.id).toString() === chatId.toString());
    }
    if (chatData) {
      selectChat(chatType, chatData);
      onClose();
    }
  };

  const handleSendNow = async (draft) => {
    if (!user) return;
    const { chatId, chatType, content } = draft;
    try {
      setSendingDraftId(draft._id);
      if (chatType === 'channel') {
        const channelId = chatId;
        const userId = user._id || user.id;
        const reqPayload = {
          channel: channelId,
          sender: userId,
          text: content,
          type: "text",
        };
        await sendChannelMessage(channelId, reqPayload);
      } else {
        const receiverId = chatId;
        const receiver = directMessages.find(d => (d._id || d.id).toString() === receiverId.toString());
        if (!receiver) throw new Error("Receiver not found");
        const sortedIds = [user._id || user.id, receiverId].sort();
        const reqPayload = {
          user1Id: sortedIds[0],
          user2Id: sortedIds[1],
          messages: [{
            sender: user.name,
            receiver: receiver.name || receiver.displayName,
            type: "text",
            message: content,
            timestamp: new Date().toISOString(),
          }]
        };
        await storeChats(reqPayload);
      }
      await deleteDraftAPI(chatId);
      await refreshData();
    } catch (error) {
      console.error("Error sending draft now:", error);
      toast.error("Failed to send draft");
    } finally {
      setSendingDraftId(null);
    }
  };

  const currentDrafts = activeTab === "drafts" ? unsentDrafts : scheduledDrafts;

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        setSelectedIndex(prev =>
          currentDrafts.length === 0 ? -1 : Math.min(prev + 1, currentDrafts.length - 1)
        );
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      }
      case 'Enter': {
        if (selectedIndex >= 0 && currentDrafts[selectedIndex]) {
          e.preventDefault();
          handleLoadDraftRef.current?.(currentDrafts[selectedIndex]);
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        onClose();
        break;
      }
      default:
        break;
    }
  }, [currentDrafts, selectedIndex, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (currentDrafts.length > 0) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex(-1);
    }
    itemRefs.current = [];
  }, [activeTab, currentDrafts.length]);

  useEffect(() => {
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  handleLoadDraftRef.current = handleLoadDraft;

  const handleDelete = (draft) => {
    toast((t) => (
      <div className="flex items-center gap-3">
        <p className="text-sm font-semibold text-text-primary">Delete this draft?</p>
        <div className="flex gap-2">
          <button
            onClick={() => { toast.dismiss(t.id); confirmDelete(draft); }}
            className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-400 text-white text-xs font-semibold transition-colors shadow-lg"
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-4 py-2 rounded-xl bg-black/10 hover:bg-black/20 text-text-primary text-xs font-semibold transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const confirmDelete = async (draft) => {
    try {
      await deleteDraftAPI(draft.chatId);
      await refetchDrafts();
      toast.success("Draft deleted");
    } catch (error) {
      console.error("Error deleting draft:", error);
      toast.error("Failed to delete draft");
    }
  };

  const renderDraftItem = (draft, index) => {
    const chatInfo = getChatInfo(draft);
    const isScheduled = !!draft.scheduledAt;
    const truncatedContent = draft.content
      ? draft.content.length > 120 ? draft.content.substring(0, 120) + '...' : draft.content
      : '(empty)';

    const isSelected = index === selectedIndex;

    return (
      <div
        key={draft._id}
        ref={(el) => (itemRefs.current[index] = el)}
        onMouseEnter={() => setSelectedIndex(index)}
        onClick={() => handleLoadDraft(draft)}
        role="option"
        aria-selected={isSelected}
        className={`group relative p-5 rounded-2xl border transition-all duration-200 cursor-pointer ${
          isSelected
            ? 'bg-primary/10 border-primary/30 shadow-xl shadow-primary/5'
            : 'bg-app-bg/50 border-border-subtle hover:bg-primary/5 hover:border-primary/20 hover:shadow-lg'
        }`}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5">
            {chatInfo.avatar ? (
              <img src={chatInfo.avatar} className="w-11 h-11 rounded-xl object-cover border-2 border-app-surface shadow-md" alt={chatInfo.name} />
            ) : (
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center border-2 border-primary/20 shadow-md">
                <AppIcon name="HashtagIcon" size={20} className="text-primary" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-text-primary truncate">{chatInfo.name}</p>
              <div className="flex items-center gap-2">
                {isScheduled && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-secondary/10 text-secondary border border-secondary/20">
                    <AppIcon name="ClockIcon" size={10} className="mr-1" />
                    {formatDate(draft.scheduledAt)}
                  </span>
                )}
                <span className="text-[10px] text-text-muted">{formatDate(draft.updatedAt || draft.createdAt)}</span>
              </div>
            </div>
            <p className="text-sm text-text-secondary leading-relaxed line-clamp-2">
              {truncatedContent}
            </p>
            {isScheduled && (
              <div className="flex items-center gap-2 mt-3">
                <AppIcon name="CalendarIcon" size={12} className="text-secondary" />
                <span className="text-xs font-medium text-secondary/80">Scheduled for {formatDate(draft.scheduledAt)}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
            <button
              onClick={(e) => { e.stopPropagation(); handleLoadDraft(draft); }}
              title="Continue editing"
              className="p-2.5 rounded-xl bg-primary/10 hover:bg-primary text-primary border border-primary/20 hover:border-primary transition-all shadow-md"
            >
              <AppIcon name="PencilSquareIcon" size={16} />
            </button>
            {isScheduled && (
              <button
                onClick={(e) => { e.stopPropagation(); handleSendNow(draft); }}
                disabled={sendingDraftId === draft._id}
                title="Send now"
                className="p-2.5 rounded-xl bg-success/10 hover:bg-success text-success border border-success/20 hover:border-success transition-all shadow-md disabled:opacity-50"
              >
                {sendingDraftId === draft._id ? (
                  <div className="w-4 h-4 border-2 border-success border-t-transparent rounded-full animate-spin" />
                ) : (
                  <AppIcon name="PaperAirplaneIcon" size={16} className="-rotate-45" />
                )}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(draft); }}
              title="Delete"
              className="p-2.5 rounded-xl bg-error/10 hover:bg-error text-error border border-error/20 hover:border-error transition-all shadow-md"
            >
              <AppIcon name="TrashIcon" size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-xl p-4 animate-fade-in">
      <div className="w-full max-w-2xl bg-app-surface rounded-3xl shadow-2xl overflow-hidden animate-fly-in">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-warning/10 rounded-full blur-3xl" />
        </div>

        <div className="relative">
          <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle bg-app-bg/20">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-warning to-orange-500 rounded-xl opacity-20 blur-lg" />
                <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-warning to-orange-500 flex items-center justify-center shadow-lg">
                  <AppIcon name="DocumentTextIcon" size={20} className="text-white" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-text-primary tracking-tight">Drafts</h2>
                <p className="text-xs text-text-muted">
                  {unsentDrafts.length} unsent • {scheduledDrafts.length} scheduled
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 rounded-xl text-text-muted hover:text-text-primary hover:bg-black/5 transition-all"
            >
              <AppIcon name="XMarkIcon" size={20} />
            </button>
          </div>

          <div className="flex border-b border-border-subtle bg-app-bg/10">
            <button
              onClick={() => setActiveTab("drafts")}
              className={`flex-1 px-6 py-4 text-sm font-semibold border-b-2 transition-all flex items-center justify-center gap-2 ${
                activeTab === "drafts"
                  ? "border-primary text-primary bg-primary/5"
                  : "border-transparent text-text-muted hover:text-text-primary"
              }`}
            >
              <AppIcon name="PencilSquareIcon" size={16} />
              Unsent
              <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/10">{unsentDrafts.length}</span>
            </button>
            <button
              onClick={() => setActiveTab("scheduled")}
              className={`flex-1 px-6 py-4 text-sm font-semibold border-b-2 transition-all flex items-center justify-center gap-2 ${
                activeTab === "scheduled"
                  ? "border-secondary text-secondary bg-secondary/5"
                  : "border-transparent text-text-muted hover:text-text-primary"
              }`}
            >
              <AppIcon name="ClockIcon" size={16} />
              Scheduled
              <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/10">{scheduledDrafts.length}</span>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 custom-scrollbar max-h-[400px]">
            {activeTab === "drafts" ? (
              unsentDrafts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <AppIcon name="PencilSquareIcon" size={28} className="text-primary/50" />
                  </div>
                  <p className="text-sm font-semibold text-text-primary mb-1">No unsent drafts</p>
                  <p className="text-xs text-text-muted">Your drafts will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {unsentDrafts.map((draft, index) => renderDraftItem(draft, index))}
                </div>
              )
            ) : scheduledDrafts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mb-4">
                  <AppIcon name="ClockIcon" size={28} className="text-secondary/50" />
                </div>
                <p className="text-sm font-semibold text-text-primary mb-1">No scheduled messages</p>
                <p className="text-xs text-text-muted">Scheduled drafts will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {scheduledDrafts.map((draft, index) => renderDraftItem(draft, index))}
              </div>
            )}
          </div>

          <div className="px-6 py-4 bg-app-bg/20 border-t border-border-subtle flex items-center gap-4 text-[10px] font-medium text-text-muted">
            <span className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-app-surface rounded-lg border border-border-subtle text-text-primary font-semibold text-[9px]">↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-app-surface rounded-lg border border-border-subtle text-text-primary font-semibold text-[9px]">Enter</kbd>
              Open
            </span>
            <span className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-app-surface rounded-lg border border-border-subtle text-text-primary font-semibold text-[9px]">Esc</kbd>
              Close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

DraftsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};